// DueActionsPage.jsx — 到期动作（SB-117，第一百一十三批）。
//
// ── 它是横幅的落点，不是横幅的替代 ──────────────────────────────────────────
// 验收判准是「他不去找也会撞见」，而**撞见发生在全局壳的横幅上**（AppShell +
// dueBanner.mjs）。这一页是横幅那颗按钮通向的地方：横幅只说"有一件迟了 N 天"，
// 落定要在这里做，因为落定要留一句话。
//
// ── 页面上没有"新建"按钮，这是刻意的 ────────────────────────────────────────
// 立行的时机是**写裁决记录的那个回合**，而那个回合发生在工作空间里（CLI）。
// 给页面加一个新建表单，等于把"记得去建"这件事又交还给人——而 SB-117 立单的
// 全部理由就是"人记不住"。
//
// ── 落定有两个主人，这一页是人的那一个；读者得分得清是谁落的 ─────────────────
// 这一页的落定走管理员的浏览器会话（后端 settle 那道闸是 admin_reach，dck_ key 一律 403）：
// 「我做了」是一句人话，脚本不借人的 key 替人说。工作空间在它自己的面上落定（bearer；
// OQ-E123 Q1：可落定任何一条、note 必填写明依据，第三百五十四批）——于是同一张表上的已落定行
// 有两种来历，**页面必须让人看得出是谁落的**：每条已落定的行挂一枚落定者标签（settledBy，
// 归类在 dueBanner.settledByOf）；工作空间落定的那条，行上斜体那句 note 就是它写的依据；
// 说不清的照实写"未记录"，不替它编一个人。
import { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Paper, Stack,
  Switch, TextField, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import HelpLink from '../components/HelpLink.jsx';
import { dueListView, dueOnLabel, settledByOf } from '../components/dueBanner.mjs';
import { useLoad } from '../hooks/useLoad.mjs';
import { useT } from '../i18n/index.mjs';

// 落定者标签（OQ-E123 Q1：页面上看得见是谁落定的）。归类只在 settledByOf 一处，这里只取词；
// 工作空间的标签着色（info），与"人"的那枚一眼分得开——两枚长得一样，等于没有标签。
function SettledByChip({ row }) {
  const t = useT();
  const by = settledByOf(row);
  if (!by) return null;
  let label;
  if (by.kind === 'workspace') {
    label = by.ref ? t('due.settledBy.workspaceRef', { ref: by.ref }) : t('due.settledBy.workspace');
  } else if (by.kind === 'person') {
    label = t('due.settledBy.person');
  } else {
    label = t('due.settledBy.unknown');
  }
  return (
    <Chip size="small" variant="outlined" color={by.kind === 'workspace' ? 'info' : 'default'}
          label={label} data-settled-by={by.kind} />
  );
}

export default function DueActionsPage() {
  const t = useT();
  const [includeSettled, setIncludeSettled] = useState(false);
  const [notes, setNotes] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(0);

  const { status, data, error, reload } = useLoad(
    () => api.dueActions({ includeSettled }), [includeSettled]);
  const loading = status === 'loading';

  const settle = async (id, state) => {
    setErr('');
    setBusy(id);
    try {
      await api.settleDueAction(id, state, notes[id] ?? '');
      setNotes((n) => ({ ...n, [id]: '' }));
      reload();
    } catch (e) {
      setErr(errKey(e) ? t(errKey(e)) : t('due.settleFailed'));
    } finally {
      setBusy(0);
    }
  };

  const rows = data?.items ?? [];
  // SB-159 口径 3：error ≠ empty（见 dueBanner.mjs 的 dueListView）。
  const view = dueListView(status, rows);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t('due.title')}
        <HelpLink section="due-actions" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('due.subtitle')}
      </Typography>
      {/* SB-191：两个"今天"都写出来。页面不自己算日期（dueBanner.test 钉着），只把服务端的
          todayUtc／todayLocal 照抄——旧服务端没给 todayUtc 就不显示这一行，而不是拿 today 顶替。 */}
      {data?.todayUtc ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('due.todays', { utc: data.todayUtc, local: data.todayLocal ?? data.today ?? '' })}
        </Typography>
      ) : null}

      <FormControlLabel
        control={<Switch checked={includeSettled}
                         onChange={(e) => setIncludeSettled(e.target.checked)} />}
        label={t('due.showSettled')}
      />

      {err ? <Alert severity="error" sx={{ my: 2 }}>{err}</Alert> : null}
      {view === 'loading' ? <CircularProgress size={24} sx={{ my: 2 }} /> : null}
      {view === 'error' ? (
        <Alert severity="error" sx={{ my: 2 }} data-due-load-failed
               action={<Button color="inherit" size="small" onClick={reload}>{t('common.retry')}</Button>}>
          {t(errKey(error, { fallback: 'due.loadFailed' }))}
        </Alert>
      ) : null}
      {view === 'empty'
        ? <Alert severity="success" sx={{ my: 2 }}>{t('due.empty')}</Alert> : null}

      <Stack spacing={2} sx={{ mt: 2 }}>
        {rows.map((r) => (
          <Paper key={r.id} variant="outlined"
                 sx={{ p: 2,
                       // 过期的行在**视觉上**也更重，而不只是排在前面：
                       // SB-117 §3 的反例说的就是"它必须比未到期的行更显眼"。
                       borderColor: r.overdue ? 'error.main' : (r.dueToday ? 'warning.main' : undefined),
                       borderWidth: r.overdue || r.dueToday ? 2 : 1 }}>
            <Stack direction="row" spacing={1} useFlexGap
                   sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              {/* 触发日带帧（SB-191）：utc 行以 Z 收尾（SOP-11 §3b 的 UTC 日写法），local 行裸写并
                  加一枚「系统时区日」——每行都挂一枚帧标签只会变成背景，Z 本身就是 utc 行的标记。 */}
              <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>{dueOnLabel(r)}</Typography>
              {r.dueFrame === 'local'
                ? <Chip size="small" variant="outlined" label={t('due.frame.local')} /> : null}
              {r.overdue
                ? <Chip size="small" color="error"
                        label={t('due.overdueBy', { days: r.daysLate })} />
                : null}
              {r.dueToday ? <Chip size="small" color="warning" label={t('due.isToday')} /> : null}
              {r.state !== 'pending'
                ? <Chip size="small" variant="outlined"
                        label={`${t('due.settledAt')}: ${r.state}`} /> : null}
              <SettledByChip row={r} />
              {r.ownerNote ? <Chip size="small" variant="outlined" label={r.ownerNote} /> : null}
            </Stack>

            <Typography variant="body1" sx={{ mt: 1 }}>{r.title}</Typography>
            {r.detail
              ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{r.detail}</Typography>
              : null}
            {r.state !== 'pending' && r.settledNote
              ? <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>{r.settledNote}</Typography>
              : null}

            {r.state === 'pending' ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                <TextField size="small" fullWidth label={t('due.settleNote')}
                           value={notes[r.id] ?? ''}
                           onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))} />
                <Button variant="contained" disabled={busy === r.id}
                        onClick={() => settle(r.id, 'done')}>{t('due.done')}</Button>
                <Button variant="outlined" color="inherit" disabled={busy === r.id}
                        onClick={() => settle(r.id, 'abandoned')}>{t('due.abandon')}</Button>
              </Stack>
            ) : null}
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
